import { useState, useEffect, useCallback } from "react";
import { useResource } from "hooks/useResource";
import { getGallery } from "services/content";
import { useSetPage } from "lib/nav";
import { mediaUrl } from "lib/media";
import SectionLabel from "components/shared/SectionLabel";
import GalleryCard from "components/shared/GalleryCard";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";

export default function GalleryPreview() {
  const setPage = useSetPage();
  const { data: photos } = useResource(getGallery);
  const preview = photos ? photos.slice(0, 6) : [];

  // Lightbox: photos are viewable straight from the homepage instead of only
  // on the gallery page. `open` is the index into `preview`, or null.
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (d) => setOpen((i) => (i === null ? i : (i + d + preview.length) % preview.length)),
    [preview.length]
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, step]);

  const current = open === null ? null : preview[open];

  return (
    <section className="bg-green-dark px-4 sm:px-10 py-[68px]">
      <div className="max-w-container mx-auto">
        <div className="flex justify-between items-end mb-9 gap-4">
          <div>
            <SectionLabel color="#8ec4b0" label="Campus Life" />
            <h2 className="font-serif text-[28px] sm:text-[38px] font-bold text-white">
              Photo Gallery
            </h2>
          </div>
          <button
            onClick={() => setPage("gallery")}
            className="bg-transparent text-gold-light border-[1.5px] border-white/30 rounded-lg px-[22px] py-2.5 font-sans font-semibold text-[13px] cursor-pointer whitespace-nowrap transition-colors hover:bg-white/10"
          >
            View All →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {!photos
            ? Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  dark
                  className="rounded-xl"
                  style={{ height: i < 3 ? 240 : 180 }}
                />
              ))
            : preview.map((p, i) => (
                <Reveal
                  key={p.id}
                  delay={i * 80}
                  style={{ gridColumn: `span ${p.span || 1}` }}
                >
                  <GalleryCard
                    item={{ ...p, span: 1 }}
                    height={i < 3 ? 240 : 180}
                    onClick={() => setOpen(i)}
                  />
                </Reveal>
              ))}
        </div>
      </div>

      {current && (
        <div
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={current.label || "Photo"}
          className="fixed inset-0 z-[2000] bg-black/85 flex items-center justify-center p-4 animate-fadeIn"
        >
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-5 w-10 h-10 rounded-full bg-white/15 border border-white/25 text-white text-2xl leading-none cursor-pointer"
          >
            ×
          </button>

          {preview.length > 1 &&
            [
              { d: -1, side: "left", ch: "‹" },
              { d: 1, side: "right", ch: "›" },
            ].map(({ d, side, ch }) => (
              <button
                key={side}
                onClick={(e) => {
                  e.stopPropagation();
                  step(d);
                }}
                aria-label={d < 0 ? "Previous photo" : "Next photo"}
                className="absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 border border-white/25 text-white text-3xl leading-none cursor-pointer hidden sm:flex items-center justify-center"
                style={{ [side]: 18 }}
              >
                {ch}
              </button>
            ))}

          {/* stopPropagation so clicking the photo itself doesn't close it */}
          <figure onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] m-0">
            <img
              src={mediaUrl(current.image)}
              alt={current.label || ""}
              className="max-w-[92vw] max-h-[78vh] object-contain rounded-lg block mx-auto"
            />
            {(current.label || current.cat) && (
              <figcaption className="text-center mt-3">
                {current.cat && (
                  <span className="block text-[10.5px] text-white/50 font-bold tracking-[0.12em] uppercase">
                    {current.cat}
                  </span>
                )}
                <span className="block text-white text-[14px] mt-1">{current.label}</span>
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </section>
  );
}
